import { AFFILIATE_MENUITEMS, MENUITEM_MORE, MENUITEMS } from '@/lib/constants';
import { MenuBarPill } from './MenuBarPill';
import pillStyles from './MenuBarPill.module.css';
import { useGlobalStore, useNavigationStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import { BottomMenuBarProps } from '../types';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';

export function BottomMenuBar({ className }: BottomMenuBarProps) {
    const breadcrumbs = useGlobalStore(state => state.breadcrumbs);
    const breadcrumbsSet = new Set(breadcrumbs.map(bread => bread.id));
    const moreMenuItem = MENUITEM_MORE;
    const { push } = useStanRouter();
    const { openMobileMoreModal, isMobileMoreModalOpen, closeMobileMoreModal } = useNavigationStore();
    const { pathname } = useLocation();
    const { user } = useUser();

    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;

    const mobileMenuItems = useMemo(() => {
        if (isAffiliateOnlySubscription) {
            // Affiliate-only users see limited menu items (AFFILIATE, INCOME, SETTINGS)
            return AFFILIATE_MENUITEMS;
        }
        return MENUITEMS.mobile.primary;
    }, [isAffiliateOnlySubscription]);

    const isPillActive = (id: string) => {
        return breadcrumbsSet.has(id) ? pillStyles.active : '';
    };

    useEffect(() => {
        const { isMobileMoreModalOpen, closeMobileMoreModal } = useNavigationStore.getState();
        if (isMobileMoreModalOpen) {
            closeMobileMoreModal();
        }
    }, [pathname]);

    const gotToRoute = (route: string) => {
        if (isMobileMoreModalOpen) {
            closeMobileMoreModal();
        }
        push(route);
    };

    // Affiliate-only users don't need "More" menu since they have limited options
    const showMoreButton = !isAffiliateOnlySubscription;
    const gridCols = showMoreButton ? 'grid-cols-5' : `grid-cols-${mobileMenuItems.length}`;

    return (
        <div
            className={`bg-brand-subtle fixed right-0 bottom-0 left-0 z-50 flex h-[var(--spacing-bottom-menu-bar)] items-center justify-between shadow-md ${className || ''}`}
        >
            <ul className={`grid w-full ${gridCols} items-center justify-around gap-2`}>
                {mobileMenuItems.map(item => (
                    <MenuBarPill
                        key={item.id}
                        text={item.title}
                        icon={item.icon}
                        onClick={() => gotToRoute(item.route)}
                        removeFill
                        horizontalView
                        className={isPillActive(item.id)}
                    />
                ))}
                {showMoreButton && (
                    <MenuBarPill
                        text={moreMenuItem.title}
                        icon={moreMenuItem.icon}
                        onClick={isMobileMoreModalOpen ? closeMobileMoreModal : openMobileMoreModal}
                        removeFill
                        horizontalView
                        className={isMobileMoreModalOpen ? pillStyles.active : ''}
                    />
                )}
            </ul>
        </div>
    );
}
